import axios from 'axios';
import { installRequestId } from '../utils/requestId';
import { getMarketplaceApiBaseUrl } from './resolveApiBaseUrl';
import { checkoutIdempotencyHeaders, resetCheckoutIdempotencyKey } from '../utils/checkoutIdempotency';

function resolveOrdersApiBase() {
  const deployBase = getMarketplaceApiBaseUrl();
  if (deployBase) return deployBase;

  const fromEnv = (process.env.REACT_APP_ORDERS_API_URL || '').trim();
  if (fromEnv) {
    if (fromEnv.endsWith('/api')) return fromEnv;
    if (fromEnv.endsWith('/')) return `${fromEnv}api`;
    return `${fromEnv}/api`;
  }
  return getMarketplaceApiBaseUrl() || 'http://localhost:8002/api';
}

const API_BASE_URL = resolveOrdersApiBase();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests (httpOnly cookies)
});
installRequestId(api);

api.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent when withCredentials: true
    // No need to manually add Authorization header
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const ordersAPI = {
  /** EGP delivery + service fees (for Paymob before points checkout) */
  getCheckoutFees: async (params = {}) => {
    const query = { ...params };
    if (query.use_wallet_for_points != null) {
      query.use_wallet_for_points = query.use_wallet_for_points ? 'true' : 'false';
    }
    const response = await api.get('/orders/checkout/fees/', { params: query });
    return response.data;
  },

  /** Start Paymob payment for fees; returns checkout_url and intent_id */
  initOrderFees: async (payload = {}, { freshAttempt = false } = {}) => {
    const response = await api.post('/orders/checkout/init-fees/', payload, {
      headers: checkoutIdempotencyHeaders(freshAttempt),
    });
    return response.data;
  },

  /** After Paymob success (webhook marks intent paid), finalize points order */
  completeOrderAfterFees: async (intentId) => {
    const response = await api.post(
      '/orders/checkout/complete-after-fees/',
      { intent_id: intentId },
      { headers: checkoutIdempotencyHeaders() },
    );
    resetCheckoutIdempotencyKey();
    return response.data;
  },

  getOrders: async () => {
    const response = await api.get('/orders/');
    return response.data;
  },

  getOrderById: async (orderId) => {
    const normalized = String(orderId || '').trim();
    if (!normalized.toUpperCase().startsWith('ORD-')) {
      throw new Error('Order URL must use ORD-* reference, not a database id');
    }
    const response = await api.get(`/orders/by-number/${encodeURIComponent(normalized)}/`);
    return response.data;
  },

  createOrder: async (orderData) => {
    const response = await api.post('/orders/', orderData);
    return response.data;
  },

  checkoutOrder: async (orderId, checkoutData) => {
    const response = await api.post(`/orders/${orderId}/checkout/`, checkoutData);
    return response.data;
  },

  submitRating: async (orderItemId, matchesDescription) => {
    const response = await api.post(`/ratings/${orderItemId}/`, {
      matches_description: matchesDescription
    });
    return response.data;
  },

  submitFeedback: async (orderItemId, itemAsDescribed, sellerResponsiveness) => {
    const response = await api.post(`/feedback/${orderItemId}/`, {
      item_as_described: itemAsDescribed,
      seller_responsiveness: sellerResponsiveness,
    });
    return response.data;
  },

  submitItemAcceptance: async (orderItemId, { accepted, matchesDescription, rejectionReason } = {}) => {
    const payload = {
      accepted,
      matches_description: matchesDescription,
    };
    if (rejectionReason != null && rejectionReason !== '') {
      payload.rejection_reason = rejectionReason;
    }
    const response = await api.post(`/orders/items/${orderItemId}/acceptance/`, payload);
    return response.data;
  },

  submitDoorPreference: async (orderItemId, { accepted, rejectionReason } = {}) => {
    const payload = { accepted };
    if (rejectionReason != null && rejectionReason !== '') {
      payload.rejection_reason = rejectionReason;
    }
    const response = await api.post(`/orders/items/${orderItemId}/door-preference/`, payload);
    return response.data;
  },

  getUserFeedbackScore: async (userId, month = null) => {
    const params = month ? `?month=${month}` : '';
    const response = await api.get(`/feedback/score/${userId}/${params}`);
    return response.data;
  },

};
